import { z } from "zod";

import { AGENT_SETUP_COPY_PROMPT, AGENT_SETUP_PROMPT_URL } from "./agent-setup";
import { activeBrand, isNeutralShellOrigin, workspaceLabelFromBrandHostname } from "./brand";
import {
  OpaqueIdSchema,
  PasswordSchema,
  PlatformRoleSchema,
  UserSummarySchema,
  UsernameSchema,
} from "./identity-schemas";
import { WorkspaceSignInOptionsSchema } from "./sign-in-policy-schemas";
import { WorkspaceInvitationSchema, WorkspaceMemberSchema } from "./workspace-user-schemas";

export * from "./identity-schemas";
export * from "./saml-schemas";
export * from "./scim-admin-schemas";
export * from "./sign-in-policy-schemas";
export * from "./tool-instance-id";
export * from "./workspace-user-schemas";

export const LoginRequestSchema = z.strictObject({
  username: UsernameSchema,
  password: PasswordSchema,
});

/**
 * A provider's stable subject identifier. This, not the email, is the durable join key: emails can
 * be renamed or recycled, a subject cannot.
 */
export const ProviderSubjectSchema = z.string().trim().min(1).max(255);

/**
 * Whether this deployment has Google sign-in configured. The login page hides the button when it is
 * false rather than offering an action that cannot succeed.
 */
export const GoogleSignInAvailabilityResponseSchema = z.strictObject({
  available: z.boolean(),
});

export const GoogleLoginFailureSchema = z.enum([
  "google_unavailable",
  "google_cancelled",
  /** The workspace turned Google sign-in off in its own policy (ADR-0061). */
  "google_disabled",
  "google_failed",
  "google_not_linked",
  "google_rate_limited",
  /** The workspace only accepts a sign-in that begins at its own address (ADR-0063). */
  "google_workspace_host_required",
  "google_workspace_access_denied",
]);

export type GoogleLoginFailure = z.infer<typeof GoogleLoginFailureSchema>;

export const WorkspacePrimaryColorSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^#[0-9a-f]{6}$/, "Enter a six-digit color such as #b42318.");

export const OrganizationSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(1).max(160),
  role: PlatformRoleSchema,
  logoVersion: z.number().int().positive().nullable(),
  faviconVersion: z.number().int().positive().nullable(),
  primaryColor: WorkspacePrimaryColorSchema.nullable(),
});

export const WorkspaceEntryBrandingResponseSchema = z.strictObject({
  workspace: z
    .strictObject({
      id: OpaqueIdSchema,
      name: z.string().min(1).max(160),
      logoVersion: z.number().int().positive().nullable(),
      faviconVersion: z.number().int().positive().nullable(),
      primaryColor: WorkspacePrimaryColorSchema.nullable(),
      /**
       * Whether this workspace offers single sign-on, and what to call it.
       *
       * Hostname-scoped and public, like the rest of this response: the workspace subdomain already
       * names the tenant, so nothing here reveals anything a visitor did not already know. The
       * button's destination is derived by the shell, never sent from here.
       */
      sso: z
        .strictObject({ displayName: z.string().min(2).max(120) })
        .nullable()
        .default(null),
      /**
       * Which sign-in methods to draw, already reduced by this workspace's policy and by whether
       * each method can work at all (ADR-0061).
       *
       * The conclusion is computed server-side rather than assembled from parts here, so the login
       * page and the authentication path cannot disagree about what is on offer. Every method is
       * re-checked when credentials or an assertion actually arrive.
       */
      signIn: WorkspaceSignInOptionsSchema,
    })
    .nullable(),
});

const RESERVED_WORKSPACE_SUBDOMAINS = new Set([
  "admin",
  "api",
  "app",
  "auth",
  "assets",
  "docs",
  "login",
  "ops",
  "staging",
  "status",
  "support",
  "www",
]);

export const WorkspaceSubdomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, "Enter at least 2 characters.")
  .max(63, "Keep the subdomain under 64 characters.")
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/, "Use lowercase letters, numbers, or dashes.")
  .refine(
    (value) => !value.startsWith("i-"),
    `Subdomains beginning with “i-” are reserved for ${activeBrand.name} tools.`,
  )
  .refine(
    // ADR-0090: `h-` hosts are integration webhook endpoints served by their own Workers, not
    // by the platform, so a workspace with that prefix could be shadowed by a more specific route.
    (value) => !value.startsWith("h-"),
    `Subdomains beginning with “h-” are reserved by ${activeBrand.name}.`,
  )
  .refine(
    // Not a policy about environments: `classifyHost` treats a trailing `-staging` as a host-label
    // suffix, so a workspace saved with one resolves to no host at all and 404s. The rule prevents
    // creating a workspace nobody can reach; the message says so without naming a deployment.
    (value) => !value.endsWith("-staging"),
    `Subdomains ending with “-staging” are reserved by ${activeBrand.name}.`,
  )
  .refine(
    (value) => !RESERVED_WORKSPACE_SUBDOMAINS.has(value),
    `That subdomain is reserved by ${activeBrand.name}.`,
  );

export const ShellOriginSchema = z
  .url()
  .max(253)
  .refine((value) => {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.port === "" &&
      url.username === "" &&
      url.password === "" &&
      url.pathname === "/" &&
      url.search === "" &&
      url.hash === "" &&
      (isNeutralShellOrigin(url.origin) ||
        WorkspaceSubdomainSchema.safeParse(workspaceLabelFromBrandHostname(url.hostname)).success)
    );
  }, `Expected a ${activeBrand.name} shell origin`);

export const WorkspaceShellPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine(
    (value) =>
      value.startsWith("/") &&
      !value.startsWith("//") &&
      !value.includes("\\") &&
      Array.from(value).every((character) => {
        const codeUnit = character.charCodeAt(0);
        return codeUnit >= 32 && codeUnit !== 127;
      }),
    `Expected a relative ${activeBrand.name} shell path`,
  );

export const WorkspaceTransitionRequestSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  intendedPath: WorkspaceShellPathSchema.default("/"),
});

export const AppShellOriginSchema = z.union([
  ShellOriginSchema,
  z.enum([
    `https://ops.${activeBrand.rootDomain}`,
    `https://ops-staging.${activeBrand.rootDomain}`,
  ]),
]);

export const WorkspaceTransitionExchangeSchema = z.strictObject({
  grant: z.string().min(32).max(256),
});

export const WorkspaceTransitionCreatedSchema = z.strictObject({
  targetOrigin: ShellOriginSchema,
  expiresAt: z.number().int().positive(),
});

export const AppIconSchema = z.enum(["quotes", "parts", "files", "listener", "table", "tool"]);
export const AppColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color");

export const AppSummarySchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  description: z.string().min(1).max(320),
  icon: AppIconSchema,
  color: AppColorSchema,
  pinned: z.boolean(),
  lastOpenedAt: z.number().int().positive().nullable(),
  technicalOrigin: z.url().refine((url) => url.startsWith("https://i-"), {
    message: `Expected an isolated ${activeBrand.name} instance origin`,
  }),
});

export const MeResponseSchema = z.strictObject({
  user: UserSummarySchema,
  organizations: z.array(OrganizationSummarySchema).max(100),
  /**
   * True when an administrator has suspended one of this account's memberships. It names no
   * workspace, so it distinguishes "your access was suspended" from "you were never given access"
   * without making inactive memberships readable.
   */
  hasSuspendedMembership: z.boolean(),
});

export const AppsResponseSchema = z.strictObject({
  organization: OrganizationSummarySchema,
  apps: z.array(AppSummarySchema).max(100),
});

export const AppletSummarySchema = z.strictObject({
  key: z
    .string()
    .min(3)
    .max(80)
    .regex(/^[a-z0-9-]+$/),
  displayName: z.string().min(2).max(120),
  description: z.string().min(10).max(320),
  icon: z.enum(["tool", "code"]),
  route: z.string().startsWith("/").min(2).max(160),
});

export const AgentSetupCapabilitySchema = z.strictObject({
  copyPrompt: z.literal(AGENT_SETUP_COPY_PROMPT),
  instructionsUrl: z.literal(AGENT_SETUP_PROMPT_URL),
});

export const AppletsResponseSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  applets: z.array(AppletSummarySchema).max(20),
  agentSetup: AgentSetupCapabilitySchema.nullable().default(null),
});

export const WorkspaceToolSchema = AppSummarySchema.omit({
  technicalOrigin: true,
  pinned: true,
  lastOpenedAt: true,
});

export const AppPreferenceUpdateSchema = z.strictObject({
  pinned: z.boolean(),
});

export const AppPreferenceResultSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  pinned: z.boolean(),
  updatedAt: z.number().int().positive(),
});

export const WorkspaceAdminResponseSchema = z.strictObject({
  organization: OrganizationSummarySchema,
  members: z.array(WorkspaceMemberSchema).max(500),
  tools: z.array(WorkspaceToolSchema).max(100),
  invitations: z.array(WorkspaceInvitationSchema).max(200),
});

export const WorkspaceMemberAccessResultSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  membershipId: OpaqueIdSchema,
  toolAccess: z.array(OpaqueIdSchema).max(100),
  updatedAt: z.number().int().positive(),
});

export const WorkspaceSettingsUpdateSchema = z.strictObject({
  name: z
    .string()
    .trim()
    .min(2, "Enter a workspace name with at least 2 characters.")
    .max(160, "Keep the workspace name under 160 characters."),
});

export const WorkspaceAppearanceUpdateSchema = z.strictObject({
  primaryColor: WorkspacePrimaryColorSchema.nullable(),
});

export const WorkspaceSubdomainUpdateSchema = z.strictObject({
  subdomain: WorkspaceSubdomainSchema,
});

export const WorkspaceToolUpdateSchema = z.strictObject({
  displayName: z
    .string()
    .trim()
    .min(2, "Enter a tool name with at least 2 characters.")
    .max(120, "Keep the tool name under 120 characters."),
  description: z
    .string()
    .trim()
    .min(10, "Describe the tool in at least 10 characters.")
    .max(320, "Keep the description under 320 characters."),
  icon: AppIconSchema,
});

export const WorkspaceAccessUpdateSchema = z.strictObject({
  granted: z.boolean(),
});

export const WorkspaceMutationResultSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  updatedAt: z.number().int().positive(),
});

export const WorkspaceToolAccessResultSchema = WorkspaceMutationResultSchema.extend({
  appInstanceId: OpaqueIdSchema,
  membershipId: OpaqueIdSchema,
  granted: z.boolean(),
}).strict();

export const WorkspaceLogoResultSchema = WorkspaceMutationResultSchema.extend({
  logoVersion: z.number().int().positive().nullable(),
}).strict();

export const WorkspaceFaviconResultSchema = WorkspaceMutationResultSchema.extend({
  faviconVersion: z.number().int().positive().nullable(),
}).strict();

export const ToolRequestSubmissionSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  whatItDoes: z
    .string()
    .trim()
    .min(10, "Describe what the tool should do in at least 10 characters.")
    .max(4000, "Keep the tool description under 4,000 characters."),
  systems: z
    .string()
    .trim()
    .min(2, "Name the systems involved, or enter “None.”")
    .max(1000, "Keep the systems description under 1,000 characters."),
});

export const ToolRequestCreatedSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  status: z.literal("submitted"),
  createdAt: z.number().int().positive(),
});

export const DeploymentHashSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);

export const PlatformHealthResponseSchema = z.strictObject({
  status: z.literal("ok"),
  service: z.literal("das-platform"),
  environment: z.enum(["production", "staging", "test"]),
  deploymentVersion: z.uuid(),
});

export const AppDetailResponseSchema = z.strictObject({
  organization: OrganizationSummarySchema,
  app: AppSummarySchema,
  deploymentHash: DeploymentHashSchema.nullable(),
});

export const TOOL_DOCUMENTATION_SECTION_IDS = [
  "overview",
  "usage",
  "troubleshooting",
  "support",
] as const;

export const ToolDocumentationSectionIdSchema = z.enum(TOOL_DOCUMENTATION_SECTION_IDS);

export const ToolDocumentationSectionSchema = z.strictObject({
  id: ToolDocumentationSectionIdSchema,
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().min(1).max(12_000),
});

export const ToolDocumentationSchema = z.strictObject({
  schemaVersion: z.literal(1),
  title: z.string().trim().min(1).max(120),
  updatedAt: z.iso.date(),
  sections: z
    .array(ToolDocumentationSectionSchema)
    .length(TOOL_DOCUMENTATION_SECTION_IDS.length)
    .refine(
      (sections) =>
        sections.every((section, index) => section.id === TOOL_DOCUMENTATION_SECTION_IDS[index]),
      "Expected an overview, usage, troubleshooting, and support section in that order",
    ),
});

export const ToolDocumentationResponseSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  documentation: ToolDocumentationSchema.nullable(),
});

export const RelativeAppPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .startsWith("/")
  .refine((value) => !value.startsWith("//"), "Network paths are not allowed")
  .refine(
    (value) => new URL(value, "https://das.invalid").origin === "https://das.invalid",
    "Expected a relative application path",
  );

export const LaunchRequestSchema = z.strictObject({
  intendedPath: RelativeAppPathSchema.default("/"),
});

export const LaunchResponseSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  technicalOrigin: z.url(),
  grant: z.string().min(32).max(256),
  expiresAt: z.number().int().positive(),
});

export const SessionExchangeRequestSchema = z.strictObject({
  grant: z.string().min(32).max(256),
});

export const SessionExchangeResponseSchema = z.strictObject({
  intendedPath: RelativeAppPathSchema,
});

export const TrustedAppContextSchema = z.strictObject({
  requestId: OpaqueIdSchema,
  userId: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  appInstanceId: OpaqueIdSchema,
  roles: z.array(PlatformRoleSchema).min(1).max(16),
});

export const BootstrapReadyMessageSchema = z.strictObject({
  type: z.literal("platform:bootstrap-ready"),
  appInstanceId: OpaqueIdSchema,
});

export const BootstrapProbeMessageSchema = z.strictObject({
  type: z.literal("platform:bootstrap-probe"),
  appInstanceId: OpaqueIdSchema,
});

export const LaunchGrantMessageSchema = z.strictObject({
  type: z.literal("platform:launch-grant"),
  appInstanceId: OpaqueIdSchema,
  grant: z.string().min(32).max(256),
});

export const AppSessionExpiredMessageSchema = z.strictObject({
  type: z.literal("platform:app-session-expired"),
});

export const ExternalNavigationRequestSchema = z.strictObject({
  type: z.literal("platform:open-external"),
  url: z.url().max(2_048),
});

export const AppMessageSchema = z.discriminatedUnion("type", [
  BootstrapReadyMessageSchema,
  AppSessionExpiredMessageSchema,
]);

export const ApiErrorSchema = z.strictObject({
  error: z.strictObject({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(240),
    requestId: OpaqueIdSchema.optional(),
  }),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type UserSummary = z.infer<typeof UserSummarySchema>;
export type OrganizationSummary = z.infer<typeof OrganizationSummarySchema>;
export type WorkspaceEntryBrandingResponse = z.infer<typeof WorkspaceEntryBrandingResponseSchema>;
export type AppSummary = z.infer<typeof AppSummarySchema>;
export type AppPreferenceUpdate = z.infer<typeof AppPreferenceUpdateSchema>;
export type AppPreferenceResult = z.infer<typeof AppPreferenceResultSchema>;
export type MeResponse = z.infer<typeof MeResponseSchema>;
export type AppsResponse = z.infer<typeof AppsResponseSchema>;
export type AppletSummary = z.infer<typeof AppletSummarySchema>;
export type AgentSetupCapability = z.infer<typeof AgentSetupCapabilitySchema>;
export type AppletsResponse = z.infer<typeof AppletsResponseSchema>;
export type WorkspaceAdminResponse = z.infer<typeof WorkspaceAdminResponseSchema>;
export type WorkspaceMemberAccessResult = z.infer<typeof WorkspaceMemberAccessResultSchema>;
export type WorkspaceSettingsUpdate = z.infer<typeof WorkspaceSettingsUpdateSchema>;
export type WorkspaceAppearanceUpdate = z.infer<typeof WorkspaceAppearanceUpdateSchema>;
export type WorkspaceSubdomainUpdate = z.infer<typeof WorkspaceSubdomainUpdateSchema>;
export type WorkspaceToolUpdate = z.infer<typeof WorkspaceToolUpdateSchema>;
export type WorkspaceAccessUpdate = z.infer<typeof WorkspaceAccessUpdateSchema>;
export type WorkspaceMutationResult = z.infer<typeof WorkspaceMutationResultSchema>;
export type WorkspaceTransitionRequest = z.infer<typeof WorkspaceTransitionRequestSchema>;
export type WorkspaceTransitionCreated = z.infer<typeof WorkspaceTransitionCreatedSchema>;
export type WorkspaceToolAccessResult = z.infer<typeof WorkspaceToolAccessResultSchema>;
export type WorkspaceLogoResult = z.infer<typeof WorkspaceLogoResultSchema>;
export type WorkspaceFaviconResult = z.infer<typeof WorkspaceFaviconResultSchema>;
export type ToolRequestSubmission = z.infer<typeof ToolRequestSubmissionSchema>;
export type ToolRequestCreated = z.infer<typeof ToolRequestCreatedSchema>;
export type AppDetailResponse = z.infer<typeof AppDetailResponseSchema>;
export type ToolDocumentationSectionId = z.infer<typeof ToolDocumentationSectionIdSchema>;
export type ToolDocumentationSection = z.infer<typeof ToolDocumentationSectionSchema>;
export type ToolDocumentation = z.infer<typeof ToolDocumentationSchema>;
export type ToolDocumentationResponse = z.infer<typeof ToolDocumentationResponseSchema>;
export type PlatformHealthResponse = z.infer<typeof PlatformHealthResponseSchema>;
export type LaunchRequest = z.infer<typeof LaunchRequestSchema>;
export type LaunchResponse = z.infer<typeof LaunchResponseSchema>;
export type TrustedAppContext = z.infer<typeof TrustedAppContextSchema>;
export type AppMessage = z.infer<typeof AppMessageSchema>;

export * from "./desktop";
